/**
 * PHARMAKON — Cross-module X · XWP-19 — the remise of a Vente libre as the cashier types it
 * (brief §9; 02_DESIGN §4.2; XD-20, XD-77). Pure: no React, no I/O.
 *
 * One « Remise » field with a « % | DA » unit; the two modes are exclusive (a window carries
 * `{ mode, valeur }` or nothing). What this module decides:
 *
 *   · reading the text: « 5 », « 5 % », « 2,5% », « 500 DA », « 1 000 », « 500da » — a « % » suffix
 *     sets the percentage unit, « DA » / « DZD » / « dinars » the amount unit, a bare number keeps
 *     the unit shown; two decimals at most, never negative, never coerced (an invalid entry is
 *     reported, not rounded);
 *   · the DZD effect and the bounds on the shared pricing engine — the server's own maths
 *     (integer centimes, `round_half_up(brut × pct / 100)`, fixed amount ≤ ⌊brut × plafond / 100⌋
 *     and ≤ brut): the ceiling is an INPUT (the référentiel's `pourcentage_max`), never a literal;
 *   · the one-key fix of a refused value (« Ajuster à 300,00 DA »);
 *   · the labels a sale's remise prints with (« Remise 5 % », « Remise (montant fixe) »).
 *
 * The server stays the authority: the window is priced again when it becomes a sale.
 */
import {
  ERREUR_REMISE_VENTE,
  MODE_REMISE_VENTE,
  calculerRemiseVente,
} from "@pharmakon/shared/pricing/remise-vente.js";
import { versCentiemesPct, versCentimes, versDinars, versPct } from "@pharmakon/shared/pricing/money.js";
import { formatDecimal, parseDecimalInput } from "./number-format.js";
import { fmtMoney } from "../config/locale.config.js";
import { REMISE_VENTE_COPIE } from "../config/remise-vente.messages.js";

export { MODE_REMISE_VENTE };

/** Decimals of both units (the wire's `multipleOf(0.01)`, Decimal(5,2) / Decimal(12,2)). */
const DECIMALES = 2;
/** The natural bound of a rate while the référentiel is unknown: 100 % (the server still caps). */
const PCT_ABSOLU_CENTIEMES = 10000;

const ESPACES = /[\s\u00a0\u202F]/g;
const SUFFIXE_POURCENT = /%$/;
const SUFFIXE_DINARS = /(?:dzd|d\.?a\.?|dinars?)$/i;

/** The other unit (Espace on an empty field, ← → on the « % | DA » control). */
export function basculerModeRemise(mode) {
  return mode === MODE_REMISE_VENTE.MONTANT ? MODE_REMISE_VENTE.POURCENTAGE : MODE_REMISE_VENTE.MONTANT;
}

/**
 * Reads what the cashier typed.
 *
 * @param {string} texte
 * @param {string} modeCourant  the unit shown (POURCENTAGE | MONTANT)
 * @returns {{ mode: string, valeur: string|null, erreur: string|null, modeImpose: boolean, vide: boolean }}
 *   `valeur` is a canonical decimal string (« 2.50 ») or null (empty / invalid); `modeImpose` when a
 *   suffix chose the unit; `vide` when there is no number at all (« », « % », « DA »).
 */
export function lireSaisieRemise(texte, modeCourant) {
  let s = String(texte ?? "").replace(ESPACES, "");
  let mode = modeCourant === MODE_REMISE_VENTE.MONTANT ? MODE_REMISE_VENTE.MONTANT : MODE_REMISE_VENTE.POURCENTAGE;
  let modeImpose = false;
  if (SUFFIXE_POURCENT.test(s)) {
    mode = MODE_REMISE_VENTE.POURCENTAGE;
    modeImpose = true;
    s = s.replace(SUFFIXE_POURCENT, "");
  } else if (SUFFIXE_DINARS.test(s)) {
    mode = MODE_REMISE_VENTE.MONTANT;
    modeImpose = true;
    s = s.replace(SUFFIXE_DINARS, "");
  }
  if (s === "") return { mode, valeur: null, erreur: null, modeImpose, vide: true };
  const r = parseDecimalInput(s, { decimals: DECIMALES, allowNegative: false });
  return { mode, valeur: r.error ? null : r.value, erreur: r.error, modeImpose, vide: false };
}

/**
 * The window remise a valid reading stands for: `{ mode, valeur }` (valeur a JS number with two
 * decimals at most — the checkpoint body), or null (empty, zero or invalid: no remise).
 */
export function remiseDeLecture(lecture) {
  if (!lecture || lecture.erreur || lecture.valeur == null) return null;
  const n = Number(lecture.valeur);
  if (!Number.isFinite(n) || n <= 0) return null;
  return { mode: lecture.mode, valeur: n };
}

/** Same remise (mode and value to the cent) — both null counts as the same. */
export function memeRemise(a, b) {
  if (!a || !b) return !a && !b;
  return a.mode === b.mode && Math.round(Number(a.valeur) * 100) === Math.round(Number(b.valeur) * 100);
}

/** A number for the field: « 5 », « 2,5 », « 1250,5 » (comma, no grouping, no trailing zeros). */
export function texteNombre(valeur) {
  if (valeur == null || valeur === "") return "";
  const t = formatDecimal(valeur, { decimals: DECIMALES, grouping: false });
  return t.includes(",") ? t.replace(/0+$/, "").replace(/,$/, "") : t;
}

/**
 * The text the field shows for a remise: while editing, the bare number (« 500 », « 2,5 »); at
 * rest, a rate as typed (« 5 ») and an amount with its cents (« 500,00 »).
 */
export function texteRemise(remise, { edition = false } = {}) {
  if (!remise) return "";
  if (edition || remise.mode === MODE_REMISE_VENTE.POURCENTAGE) return texteNombre(remise.valeur);
  return formatDecimal(remise.valeur, { decimals: DECIMALES });
}

/** « 5 », « 2,5 » — a rate for a label. */
export const texteTaux = (pct) => texteNombre(pct);

/**
 * The DZD effect of a remise on a basket and whether it can be applied (the server's rules).
 *
 * @param {{ mode: string, valeur: number|string }|null} remise
 * @param {{ brut: number|string, plafondPct: number|null, modeAffiche?: string|null }} contexte
 *   brut in DA (the lines in the sale), plafondPct the référentiel's `pourcentage_max` (null while
 *   unknown), modeAffiche the unit the field shows while there is no remise (for the hint)
 * @returns {{
 *   montant: number,            the DZD effect (0 when refused or none)
 *   erreur: string|null,        an ERREUR_REMISE_VENTE code
 *   message: string|null,       its French wording (the server's)
 *   plafondMontant: number|null the largest fixed amount on this basket (DA), null without a ceiling
 *   ajustement: { mode, valeur }|null  the closest allowed remise, for « Ajuster à … »
 *   indication: string          « Max 30 % » / « Max 300,00 DA (30 % du montant) »
 * }}
 */
export function evaluerRemise(remise, { brut, plafondPct, modeAffiche = null }) {
  const brutCentimes = versCentimes(brut ?? 0);
  const plafondConnu = plafondPct != null && Number.isFinite(Number(plafondPct));
  const plafondCentiemes = plafondConnu ? versCentiemesPct(plafondPct) : PCT_ABSOLU_CENTIEMES;
  const maxTexte = texteTaux(versPct(plafondCentiemes));
  const mode = remise?.mode ?? null;

  const r = calculerRemiseVente({
    brut_centimes: brutCentimes,
    mode: remise ? mode : null,
    pct_centiemes: mode === MODE_REMISE_VENTE.POURCENTAGE ? versCentiemesPct(remise.valeur) : 0,
    montant_centimes: mode === MODE_REMISE_VENTE.MONTANT ? versCentimes(remise.valeur) : 0,
    plafond_pct_centiemes: plafondCentiemes,
  });
  const plafondMontant = plafondConnu && brutCentimes > 0 ? versDinars(r.plafond_montant_centimes) : null;
  const indication =
    (mode ?? modeAffiche) === MODE_REMISE_VENTE.MONTANT
      ? plafondConnu
        ? brutCentimes > 0
          ? REMISE_VENTE_COPIE.plafond_montant(fmtMoney(versDinars(r.plafond_montant_centimes)), maxTexte)
          : REMISE_VENTE_COPIE.plafond_montant_sans_brut(maxTexte)
        : ""
      : plafondConnu
        ? REMISE_VENTE_COPIE.plafond_pct(maxTexte)
        : "";
  const base = { montant: 0, erreur: null, message: null, plafondMontant, ajustement: null, indication };

  if (!remise) return base;
  // A fixed amount on an empty basket is not a mistake yet: the bound applies once lines exist
  // (and again when the window becomes a sale).
  if (mode === MODE_REMISE_VENTE.MONTANT && brutCentimes === 0) return base;
  if (!r.erreur) return { ...base, montant: versDinars(r.remise_centimes) };

  const fmt = REMISE_VENTE_COPIE.erreurs[r.erreur];
  const message = fmt
    ? fmt({ max: maxTexte, brut: fmtMoney(versDinars(brutCentimes)), plafond: fmtMoney(versDinars(r.plafond_montant_centimes)) })
    : null;
  let ajustement = null;
  if (r.erreur === ERREUR_REMISE_VENTE.POURCENTAGE_HORS_BORNES) {
    ajustement = { mode: MODE_REMISE_VENTE.POURCENTAGE, valeur: versPct(plafondCentiemes) };
  } else if (
    (r.erreur === ERREUR_REMISE_VENTE.MONTANT_HORS_PLAFOND || r.erreur === ERREUR_REMISE_VENTE.MONTANT_SUPERIEUR_BRUT) &&
    r.plafond_montant_centimes > 0
  ) {
    ajustement = { mode: MODE_REMISE_VENTE.MONTANT, valeur: versDinars(r.plafond_montant_centimes) };
  }
  return { ...base, erreur: r.erreur, message, ajustement };
}

/** « Ajuster à 30 % » / « Ajuster à 300,00 DA ». */
export function libelleAjustement(ajustement) {
  if (!ajustement) return null;
  return REMISE_VENTE_COPIE.ajuster(
    ajustement.mode === MODE_REMISE_VENTE.MONTANT ? fmtMoney(ajustement.valeur) : `${texteTaux(ajustement.valeur)} %`,
  );
}

/**
 * The label of a remise (totals, receipt): « Remise 5 % », « Remise (montant fixe) », « Remise ».
 * @param {{ mode: string|null, pct?: number|string|null }} r
 */
export function libelleRemise({ mode, pct } = {}) {
  if (mode === MODE_REMISE_VENTE.MONTANT) return REMISE_VENTE_COPIE.libelle_montant;
  if (mode === MODE_REMISE_VENTE.POURCENTAGE && Number(pct) > 0) return REMISE_VENTE_COPIE.libelle_pct(texteTaux(pct));
  return REMISE_VENTE_COPIE.libelle;
}

/**
 * The label of a recorded sale's remise. A sale written before x06 has no `remise_mode`: a
 * positive `remise_globale_pct` reads as a rate (the x06 back-fill rule).
 */
export function libelleRemiseVente(vente) {
  const pct = Number(vente?.remise_globale_pct ?? 0);
  const mode = vente?.remise_mode ?? (pct > 0 ? MODE_REMISE_VENTE.POURCENTAGE : null);
  return libelleRemise({ mode, pct });
}
